import { supabase } from "@/lib/supabase";

/**
 * BW20.4c — Travel-doc -> calendar event fan-out.
 *
 * When a flight_ticket / train_ticket / hotel_booking /
 * restaurant_reservation document is saved, mirror the relevant timestamps
 * in metadata into one or two calendar_event rows linked back to the
 * document via document_id. Cascade delete handles cleanup; on edit the
 * caller can re-run this helper after wiping prior events for the doc.
 *
 * Returns the number of events created. Dedupes against existing events
 * for the same document_id at the same starts_at.
 */

const TRAVEL_KINDS = new Set([
  "flight_ticket",
  "train_ticket",
  "hotel_booking",
  "restaurant_reservation",
]);

export type TravelDocInput = {
  id: string;
  user_id: string;
  kind: string;
  title: string;
  metadata: Record<string, unknown>;
};

export type CreatedEvent = {
  starts_at: string;
  title: string;
  source: "document_travel";
};

export async function syncDocumentToCalendarEvents(
  doc: TravelDocInput,
  options: { wipeFirst?: boolean } = {},
): Promise<{ created: number; skipped_existing: number; events: CreatedEvent[] }> {
  if (!TRAVEL_KINDS.has(doc.kind)) {
    return { created: 0, skipped_existing: 0, events: [] };
  }

  if (options.wipeFirst) {
    const sb = supabase as unknown as {
      from: (t: string) => {
        delete: () => { eq: (k: string, v: string) => Promise<{ error: Error | null }> };
      };
    };
    await sb.from("calendar_event").delete().eq("document_id", doc.id);
  }

  const events = buildEventsForDoc(doc);
  if (events.length === 0) {
    return { created: 0, skipped_existing: 0, events: [] };
  }

  // Dedupe: load existing events for the doc and skip overlap by starts_at.
  const sb = supabase as unknown as {
    from: (t: string) => {
      select: (cols: string) => {
        eq: (k: string, v: string) => Promise<{ data: Array<{ starts_at: string }> | null; error: Error | null }>;
      };
      insert: (rows: unknown) => Promise<{ error: Error | null }>;
    };
  };

  const { data: existing } = await sb
    .from("calendar_event")
    .select("starts_at")
    .eq("document_id", doc.id);
  const existingStarts = new Set((existing ?? []).map((r) => r.starts_at));

  const inserts = events
    .filter((e) => !existingStarts.has(e.starts_at))
    .map((e) => ({
      user_id: doc.user_id,
      starts_at: e.starts_at,
      ends_at: e.ends_at,
      tz: e.tz ?? "UTC",
      title: e.title,
      description: e.description ?? "",
      location: e.location ?? "",
      source: "document_travel" as const,
      document_id: doc.id,
      link: e.link ?? {},
      reminders: [],
    }));

  if (inserts.length === 0) {
    return { created: 0, skipped_existing: events.length, events: [] };
  }

  const { error } = await sb.from("calendar_event").insert(inserts);
  if (error) throw error;

  return {
    created: inserts.length,
    skipped_existing: events.length - inserts.length,
    events: inserts.map((e) => ({
      starts_at: e.starts_at,
      title: e.title,
      source: "document_travel" as const,
    })),
  };
}

type DerivedEvent = {
  starts_at: string;
  ends_at: string;
  tz?: string;
  title: string;
  description?: string;
  location?: string;
  link?: Record<string, unknown>;
};

function buildEventsForDoc(doc: TravelDocInput): DerivedEvent[] {
  const m = doc.metadata ?? {};
  const str = (k: string): string | null => {
    const v = m[k];
    return typeof v === "string" && v.trim() ? v : null;
  };
  switch (doc.kind) {
    case "flight_ticket": {
      const dep = str("departure_at");
      const arr = str("arrival_at");
      const flightNo = str("flight_number") ?? "";
      const airline = str("airline") ?? "";
      const origin = str("departure_airport") ?? "";
      const dest = str("arrival_airport") ?? "";
      const out: DerivedEvent[] = [];
      if (dep) {
        out.push({
          starts_at: dep,
          ends_at: arr ?? dep,
          title: `Flight ${airline} ${flightNo} ${origin} -> ${dest}`.trim(),
          description: doc.title,
          location: origin,
          link: { document_id: doc.id, leg: "departure" },
        });
      }
      if (arr && arr !== dep) {
        out.push({
          starts_at: arr,
          ends_at: arr,
          title: `Arrive ${dest} (${airline} ${flightNo})`.trim(),
          description: doc.title,
          location: dest,
          link: { document_id: doc.id, leg: "arrival" },
        });
      }
      return out;
    }
    case "train_ticket": {
      const dep = str("departure_at");
      const arr = str("arrival_at");
      const operator = str("operator") ?? "";
      const trainNo = str("train_number") ?? "";
      const origin = str("origin") ?? "";
      const dest = str("destination") ?? "";
      const out: DerivedEvent[] = [];
      if (dep) {
        out.push({
          starts_at: dep,
          ends_at: arr ?? dep,
          title: `Train ${operator} ${trainNo} ${origin} -> ${dest}`.trim(),
          description: doc.title,
          location: origin,
          link: { document_id: doc.id, leg: "departure" },
        });
      }
      if (arr && arr !== dep) {
        out.push({
          starts_at: arr,
          ends_at: arr,
          title: `Arrive ${dest} (${operator} ${trainNo})`.trim(),
          description: doc.title,
          location: dest,
          link: { document_id: doc.id, leg: "arrival" },
        });
      }
      return out;
    }
    case "hotel_booking": {
      const checkin = str("checkin_at");
      const checkout = str("checkout_at");
      const hotel = str("hotel_name") ?? doc.title;
      const address = str("address") ?? "";
      const out: DerivedEvent[] = [];
      if (checkin) {
        out.push({
          starts_at: checkin,
          ends_at: checkin,
          title: `Check in: ${hotel}`,
          description: doc.title,
          location: address,
          link: { document_id: doc.id, leg: "checkin" },
        });
      }
      if (checkout) {
        out.push({
          starts_at: checkout,
          ends_at: checkout,
          title: `Check out: ${hotel}`,
          description: doc.title,
          location: address,
          link: { document_id: doc.id, leg: "checkout" },
        });
      }
      return out;
    }
    case "restaurant_reservation": {
      const reserved = str("reserved_at");
      const restaurant = str("restaurant_name") ?? doc.title;
      const address = str("address") ?? "";
      if (!reserved) return [];
      return [
        {
          starts_at: reserved,
          ends_at: reserved,
          title: `Reservation: ${restaurant}`,
          description: doc.title,
          location: address,
          link: { document_id: doc.id, leg: "reservation" },
        },
      ];
    }
    default:
      return [];
  }
}
